/**
 * Утилиты для работы с формой подписки робоэдвайзера
 */

import { SubscriptionFormData, ClientData } from '../types/subscriptionForm.types';

const STORAGE_KEY = 'subscription_form_data';

export const initialFormData: SubscriptionFormData = {
  phone: '',
  lastName: '',
  name: '',
  middleName: '',
  passportSeries: '',
  passportNumber: '',
  passportIssuer: '',
  passportAddress: '',
  inn: '',
  snils: '',
  strategyAgreement: true,
  offerAgreement: true,
  personalDataConsent: true,
};

/**
 * Извлекает данные из client объекта и преобразует их в формат SubscriptionFormData
 */
export const extractClientData = (
  client: ClientData | null | undefined
): Partial<SubscriptionFormData> => {
  if (!client) return {};

  const clientData: Partial<SubscriptionFormData> = {};
  if (client.phone) clientData.phone = client.phone;
  if (client.first_name) clientData.name = client.first_name;
  if (client.last_name) clientData.lastName = client.last_name;
  if (client.middle_name) clientData.middleName = client.middle_name;

  return clientData;
};

/**
 * Загружает данные из localStorage
 */
export const loadStoredData = (): Partial<SubscriptionFormData> | null => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;

  try {
    return JSON.parse(stored) as Partial<SubscriptionFormData>;
  } catch (e) {
    console.error('Error parsing stored form data:', e);
    return null;
  }
};

/**
 * Проверяет, является ли значение пустым
 */
export const isEmpty = (value: any): boolean => {
  return value === null || value === undefined || value === '';
};

/**
 * Заполняет пустые поля в target данными из source
 */
export const fillEmptyFields = (
  target: Partial<SubscriptionFormData>,
  source: Partial<SubscriptionFormData>
): Partial<SubscriptionFormData> => {
  const result = { ...target };
  if (source.phone && isEmpty(result.phone)) result.phone = source.phone;
  if (source.name && isEmpty(result.name)) result.name = source.name;
  if (source.lastName && isEmpty(result.lastName)) result.lastName = source.lastName;
  if (source.middleName && isEmpty(result.middleName)) result.middleName = source.middleName;
  return result;
};

/**
 * Объединяет данные с учетом приоритетов:
 * 1. initialData (наивысший приоритет)
 * 2. storedData (из localStorage)
 * 3. clientData (из Redux, заполняет только пустые поля)
 */
export const mergeFormData = (
  initialData?: Partial<SubscriptionFormData>,
  storedData?: Partial<SubscriptionFormData> | null,
  clientData?: Partial<SubscriptionFormData>
): SubscriptionFormData => {
  // Если есть initialData, используем их с приоритетом
  if (initialData && Object.keys(initialData).length > 0) {
    return { ...initialFormData, ...clientData, ...initialData };
  }

  // Иначе используем сохраненные данные и заполняем пустые поля из client
  const baseData = storedData || {};
  const merged = fillEmptyFields(baseData, clientData || {});

  return { ...initialFormData, ...merged };
};

/**
 * Сохраняет данные формы в localStorage
 */
export const saveFormData = (data: SubscriptionFormData): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Error saving form data to localStorage:', e);
  }
};

/**
 * Удаляет сохраненные данные формы из localStorage
 */
export const clearFormData = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('Error clearing form data from localStorage:', e);
  }
};

/**
 * Валидирует форму - проверяет, что все обязательные поля заполнены
 */
export const validateForm = (formData: SubscriptionFormData): boolean => {
  return !!(
    formData.phone &&
    formData.lastName &&
    formData.name &&
    formData.passportSeries &&
    formData.passportNumber &&
    formData.passportIssuer &&
    formData.passportAddress &&
    formData.inn &&
    formData.snils &&
    formData.strategyAgreement &&
    formData.offerAgreement &&
    formData.personalDataConsent
  );
};
